import './App.css';
import Ajv from "ajv";
import logo from './topcon-logo.svg';
import {
  BrowserRouter as Router
} from 'react-router-dom';
import {
  TeSpacing,
  TeText
} from '@topcon/te-web-react';
import { useState, useCallback, useMemo, useEffect, useReducer, Fragment } from 'react';
import credentialRequestSchema from "./credential-request.schema.json"
import ValidatedInput from './components/ValidatedInput/ValidatedInput';
import ValidatedCheckbox from './components/ValidatedCheckbox/ValidatedCheckbox';
import { InvokeCommand } from '@aws-sdk/client-lambda'
import lambdaClient from './aws/lambda-client.js'
import parse from 'html-react-parser'

const ajv = new Ajv({ allErrors: true, verbose: true });
const validate = ajv.compile(credentialRequestSchema);

const usePromiseReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { "state": "loading" }
    case "setResponse":
      return { "state": "loaded", "value": action.value }
    case "setError":
      return { "state": "failed", "error": action.error }
    case "clear":
      return { "state": "noPromise" }
    default:
      return state
  }
}

const usePromise = (promise) => {
  const [state, dispatch] = useReducer(usePromiseReducer, { "state": "noPromise" });
  useEffect(() => {
    if (promise === null) {
      dispatch({ type: "clear" });
    }
    else // added this so that we can call usePromise with a null promise and just dispatch the clear state
    {
      dispatch({ type: "setLoading" })
      promise.then((value) => {
        const jsonString = Buffer.from(value["Payload"]).toString('utf8')
        const parsedData = JSON.parse(jsonString)
        if (parsedData["statusCode"] !== 200) {
          var returnMessage = "<ul>"
          if (parsedData["body"].hasOwnProperty('errors')) {
            Object.keys(parsedData["body"]["errors"]).forEach(function (key) {
              var msg = "<li>Your " + humanReadableJsonField(key) + " is " + parsedData["body"]["errors"][key] + ".</li>"
              returnMessage += msg;
            })
          }
          returnMessage += "</ul>"
          dispatch({ type: "setError", error: returnMessage })
        } else {
          dispatch({ type: "setResponse", value: parsedData["body"] })
        }
      }).catch((error) => {
        dispatch({ type: "setError", error })
      })
    }
  }, [promise])
  return state
};

const humanReadableJsonField = (field) => {
  switch (field) {
    case "companyName":
      return "company name"
    case "companyAddress":
      return "company address"
    case "companyDescription":
      return "company description"
    case "contactName":
      return "contact name"
    case "contactEmail":
      return "contact email"
    case "contactPhone":
      return "contact phone"
    case "applicationName":
      return "application name"
    case "applicationDescription":
      return "application description"
    case "applicationWebsite":
      return "application website"
    case "agreeLegal":
      return "agree Legal"
    default:
      return "unknown field"
  }
}

function App() {

  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyDescription, setCompanyDescription] = useState("")
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [applicationName, setApplicationName] = useState("");
  const [applicationDescription, setApplicationDescription] = useState("");
  const [applicationWebsite, setApplicationWebsite] = useState("");
  const [pkceRequired, setPkceRequired] = useState(false);
  const [sha256Incapable, setSha256Incapable] = useState(false);
  const [agreeLegal, setAgreeLegal] = useState(false);

  const [submitPromise, setSubmitPromise] = useState(null);
  const submitState = usePromise(submitPromise)

  const onCompanyNameChange = useCallback(e => setCompanyName(e.target.value), [setCompanyName]);
  const onCompanyAddressChange = useCallback(e => setCompanyAddress(e.target.value), [setCompanyAddress]);
  const onCompanyDescriptionChange = useCallback(e => setCompanyDescription(e.target.value), [setCompanyDescription]);
  const onContactNameChange = useCallback(e => setContactName(e.target.value), [setContactName]);
  const onContactEmailChange = useCallback(e => setContactEmail(e.target.value), [setContactEmail]);
  const onContactPhoneChange = useCallback(e => setContactPhone(e.target.value), [setContactPhone]);
  const onApplicationNameChange = useCallback(e => setApplicationName(e.target.value), [setApplicationName]);
  const onApplicationDescriptionChange = useCallback(e => setApplicationDescription(e.target.value), [setApplicationDescription]);
  const onApplicationWebsiteChange = useCallback(e => setApplicationWebsite(e.target.value), [setApplicationWebsite]);
  const onPkceRequiredChange = useCallback(e => setPkceRequired(e.target.value), [setPkceRequired]);
  const onSha256Incapable = useCallback(e => setSha256Incapable(e.target.value), [setSha256Incapable]);
  const onAgreeLegal = useCallback(e => setAgreeLegal(e.target.checked), [setAgreeLegal]);

  const [errors, setErrors] = useState([]);

  const handleSubmit = useCallback(e => {
    const data = {
      "companyName": companyName,
      "companyAddress": companyAddress,
      "companyDescription": companyDescription,
      "contactName": contactName,
      "contactEmail": contactEmail,
      "contactPhone": contactPhone,
      "applicationName": applicationName,
      "applicationDescription": applicationDescription,
      "applicationWebsite": applicationWebsite,
      "pkceRequired" : pkceRequired,
      "sha256Incapable" : sha256Incapable,
      "agreeLegal" : agreeLegal
    };
    if (validate(data)) {
      setErrors([]);

      const payload = new TextEncoder("utf-8").encode(JSON.stringify({
        Payload: data
      }))

      const params = {
        FunctionName: "requestOauthCredentials",
        InvocationType: "RequestResponse",
        LogType: "None",
        Payload: payload
      }

      setSubmitPromise(lambdaClient.send(new InvokeCommand(params)))

    } else {
      setErrors(validate.errors);
    }

  }, [companyName, companyAddress, companyDescription, contactName, contactEmail, contactPhone, applicationName, applicationDescription, applicationWebsite, pkceRequired, sha256Incapable, agreeLegal]);

  // will rerun everytime errors changes
  const validation_state = useMemo(() => {
    const validation_results = {
      "companyName": { "hasError": false, "errorText": "" },
      "companyAddress": { "hasError": false, "errorText": "" },
      "companyDescription": { "hasError": false, "errorText": "" },
      "contactName": { "hasError": false, "errorText": "" },
      "contactEmail": { "hasError": false, "errorText": "" },
      "contactPhone": { "hasError": false, "errorText": "" },
      "applicationName": { "hasError": false, "errorText": "" },
      "applicationDescription": { "hasError": false, "errorText": "" },
      "applicationWebsite": { "hasError": false, "errorText": "" },
      "agreeLegal": { "hasError": false, "errorText": "" }
    }
    errors.forEach((error) => {
      validation_results[error.dataPath.substr(1)] = { "hasError": true, "errorText": error.message }
    })
    return validation_results
  }, [errors])

  const renderState = (state, promiseMutator) => {
    switch (state["state"]) {
      case "loading":
        return (
          <>
            <div className="alert alert-info" role="alert">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </>
        )
      case "loaded":
        return (
          <>
            <div className="alert alert-success" role="alert">
              <p><em>Thank you.</em> Your request has been submitted and we'll be in contact shortly.</p>
              <button type="button" className="btn btn-outline-primary" onClick={() => promiseMutator(null)} data-toggle="tooltip" data-placement="top">Make Another Request.</button>
            </div>
          </>
        )
      case "failed":
        return (
          <>
            <div className="alert alert-danger" role="alert">
              <p>Unfortunately we couldn't process your request:</p>
              {parse(state["error"])}
              <button type="button" className="btn btn-outline-primary" onClick={() => promiseMutator(null)} data-toggle="tooltip" data-placement="top">Try Again.</button>
            </div>
          </>
        )
      case "noPromise":
      default:
        return (
          <>
            <form>
              <div className="container">
                <p className="lead">Company Details <small className="text-muted">You'll only need to register your company with us once.</small></p>
                <hr />
                <div className="row">
                  <div className="col-4">
                    <ValidatedInput
                      label="Company Name"
                      id="companyNameInputComp"
                      type="text"
                      hasError={validation_state["companyName"].hasError}
                      onChange={onCompanyNameChange}
                      errorText="Please enter your company name."
                      value={companyName}
                      maxLength={50} />
                  </div>
                  <div className="col-8">
                    <ValidatedInput
                      label="Company Address"
                      id="companyAddressInputComp"
                      type="text"
                      hasError={validation_state["companyAddress"].hasError}
                      onChange={onCompanyAddressChange}
                      errorText="Please tell us where your company is based."
                      value={companyAddress}
                      maxLength={50} />
                  </div>
                </div>
                <div className="row">
                  <ValidatedInput
                    label="Company Description"
                    id="companyDescriptionInputComp"
                    type="text"
                    hasError={validation_state["companyDescription"].hasError}
                    onChange={onCompanyDescriptionChange}
                    errorText="Please tell us a bit about what you do."
                    description="A little about what your company does."
                    value={companyDescription}
                    maxLength={50} />
                </div>
                <br/>
                <p className="lead">Contact Details <small className="text-muted">The best person for us to send your credentials to.</small></p>
                <hr />
                <div className="row">
                  <div className="col-4">
                    <ValidatedInput
                      label="Contact Name"
                      id="contactNameInputComp"
                      type="text"
                      hasError={validation_state["contactName"].hasError}
                      onChange={onContactNameChange}
                      errorText="Don't be shy, we'd like to help you build great things."
                      value={contactName}
                      maxLength={50} />
                  </div>
                  <div className="col-4">
                    <ValidatedInput
                      label="Contact Email"
                      id="contactEmailInputComp"
                      type="email"
                      hasError={validation_state["contactEmail"].hasError}
                      onChange={onContactEmailChange}
                      errorText="We'll need your email to send your credentials."
                      value={contactEmail}
                      maxLength={50} />
                  </div>
                  <div className="col-4">
                    <ValidatedInput
                      label="Contact Phone"
                      id="contactPhoneInputComp"
                      type="text"
                      hasError={validation_state["contactPhone"].hasError}
                      onChange={onContactPhoneChange}
                      errorText="Just in case we need to get in touch."
                      value={contactPhone}
                      maxLength={50} />
                  </div>
                </div>
                <br/>
                <div>
                  <p className="lead">Application Details <small className="text-muted">The software you'd like to connect with Sitelink3D v2.</small></p>
                  <hr />
                  <ValidatedInput
                    label="Application Description"
                    id="applicationDescriptionInputComp"
                    type="text"
                    hasError={validation_state["applicationDescription"].hasError}
                    onChange={onApplicationDescriptionChange}
                    errorText="Please describe your product."
                    value={applicationDescription}
                    maxLength={50} />
                </div>
                <div className="row">
                  <div className="col">
                    <ValidatedInput
                      label="Application or Product Name"
                      id="applicationNameInputComp"
                      type="text"
                      hasError={validation_state["applicationName"].hasError}
                      onChange={onApplicationNameChange}
                      errorText="Please enter a product name."
                      value={applicationName}
                      maxLength={50} />
                  </div>
                  <div className="col">
                    <ValidatedInput
                      label="Application Website"
                      id="applicationWebsiteInputComp"
                      type="text"
                      hasError={validation_state["applicationWebsite"].hasError}
                      onChange={onApplicationWebsiteChange}
                      errorText="Somewhere we can learn more."
                      value={applicationWebsite}
                      maxLength={50} />
                  </div>
                </div>
                <br/>
                <div>
                  <p className="lead">Additional Settings <small className="text-muted">Some additional options. Leave as default if unsure.</small></p>
                  <hr />                  
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="form-switch-pkce-required" onChange={onPkceRequiredChange} />
                    <label className="form-check-label" htmlFor="form-switch-pkce-required">PKCE Required</label>
                    <div>
                      <small className="form-text text-muted">PKCE is required when client software is unable to protect its secret.</small>
                    </div>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="form-switch-sha256-incapable" onChange={onSha256Incapable} />
                    <label className="form-check-label" htmlFor="form-switch-sha256-incapable">SHA256 Incapable</label>
                    <div>
                      <small className="form-text text-muted">If a client is incapable of performing a sha256 hash they will be permitted to use <em>plain</em> as the <em>code_challenge_method</em>.</small>
                    </div>
                  </div>
                </div>
                <br/>
                <div>
                  <p className="lead">API License Agreement <small className="text-muted"> Feel free to contact us for more information or to discuss custom agreements.</small></p>
                  <hr />     
                  <ValidatedCheckbox
                      label="Agree with API License"
                      id="form-switch-agree-legal"
                      hasError={validation_state["agreeLegal"].hasError}
                      onChange={onAgreeLegal}
                      errorText="Please accept our API license agreement. If you have any concerns, please email us at sitelink3d-api-support@topcon.com to discuss." />
                  <div>
                    <small className="form-text text-muted">By submitting this form, you acknowledge that you have read and agree to the <em>Topcon Sitelink3D API License Agreement</em> found <a href="/static/legals/Sitelink3D_API_License.pdf">here</a> and the <em>Topcon Group privacy statement</em> found at <a href="https://www.topconpositioning.com/">Topcon Positioning Systems, Inc.</a>.</small>
                  </div>

                </div>
                <br/>
                <button type="button" className="btn btn-outline-primary" onClick={handleSubmit} data-toggle="tooltip" data-placement="top">Submit</button>
                <TeSpacing direction="y" size="20"></TeSpacing>
              </div>
            </form>
          </>
        )
    }
  }

  return (
    <Router>
      <div className="App">
        <nav className="navbar sticky-top bg-primary navbar-dark mb-3">
          <div className="container">
            <img className="App-topcon-logo" alt="Topcon Logo" src={logo} />
            <TeText size="24" color="#FFFFFF" >Sitelink3D v2 OAuth Client Request</TeText>
          </div>
        </nav>
        <div className="container">
          <p className="lead">Welcome to the Topcon OAuth Client Credentials request tool.</p>
          <p>Complete and submit the form below to receive a client identifier and secret for use with our Sitelink3D v2 API. See our documentation <a href="https://github.com/Sitelink3D-v2-Developer/sitelink3dv2-api-documentation">here</a> for a detailed description.</p>
          {renderState(submitState, setSubmitPromise)}
        </div>
      </div>
    </Router >
  );
}

export default App;
